import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch } from '../../lib/api'
import { ThumbUpIcon } from '../icons/ThumbUpIcon'
import { ThumbDownIcon } from '../icons/ThumbDownIcon'

/* ─── 👍/👎 interest vote on a Preview MCP product ───
   Shared by MCP Config (/mambu-mcp) and MCP Analytics (/mcp-analytics). Posts to
   the existing shared endpoint (R-OBS-1, agent-studio-prd.md:208); nothing is
   persisted — the structured log line is forwarded to Grafana by the deployed
   OTel logs pipeline.
   Prototype: agent-studio.html:5786-5789 (analytics), :5480-5483 (config). */

export interface PreviewInterestVoteProps {
  /** The analytics identifier posted to the backend — build it with
   *  mcpPreviewVoteFeature() (lib/mcpVoteFeature.ts), which documents the
   *  surface-suffix convention. Never a localised string. */
  feature: string
  /** Called after the vote settles, successfully or not, so the page can show its toast. */
  onVoted: () => void
  /** The current vote for this product, owned by the PAGE and keyed by `item.key` —
   *  NOT local state here. Both the grid and list renderings of the same product are
   *  mounted at once (the page swaps visibility with hidden/active, it never unmounts
   *  either), so two `PreviewInterestVote` instances render one product; only a
   *  lifted, shared value keeps their highlighted state in sync. */
  voted: 'up' | 'down' | null
  /** Called synchronously on click — before the request settles — so the page can
   *  update its shared `voted` record and both renderings highlight immediately. */
  onVote: (direction: 'up' | 'down') => void
}

export function PreviewInterestVote({ feature, onVoted, voted, onVote }: PreviewInterestVoteProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()

  const voteMutation = useMutation({
    mutationFn: (direction: 'up' | 'down') =>
      apiFetch<void>('/api/v1/global-settings/preview-interest-vote', {
        method: 'POST',
        body: JSON.stringify({ feature, direction }),
      }),
    // The vote is advisory: a transport failure must not be surfaced to the admin
    // as one. Both paths thank them and both mark the button as voted.
    onSuccess: onVoted,
    onError: onVoted,
  })

  function handleVote(direction: 'up' | 'down') {
    onVote(direction)
    voteMutation.mutate(direction)
  }

  return (
    <>
      <button
        type="button"
        className={`vote-btn${voted === 'up' ? ' voted' : ''}`}
        onClick={() => handleVote('up')}
      >
        <ThumbUpIcon />
        {t('preview.interested')}
      </button>
      <button
        type="button"
        className={`vote-btn${voted === 'down' ? ' voted' : ''}`}
        onClick={() => handleVote('down')}
      >
        <ThumbDownIcon />
        {t('preview.notForUs')}
      </button>
    </>
  )
}
